import { useState, useEffect } from 'react';
import { AxiosResponse } from 'axios';
import { Plus, Edit2, Trash2, Search, Check, X, AlertCircle, Loader2, Upload, Video, FileText, Clock, MessageSquare } from 'lucide-react';
import { 
  fetchAllQuestions, 
  createQuestion, 
  updateQuestion, 
  deleteQuestion, 
  toggleQuestionStatus,
  fetchVideoQuestions,
  createVideoQuestion,
  updateVideoQuestion,
  deleteVideoQuestion,
  fetchGeneralQuestions,
  createGeneralQuestion,
  updateGeneralQuestion,
  deleteGeneralQuestion
} from 'api/apiHelper';
import Backdrop from 'components/Backdrop';
import ErrorBox from 'components/ErrorBox';
import { toast } from "react-toastify";

// Video Question Interface
interface VideoQuestion {
  id: number;
  question_text: string;
  question_order: number;
  time_limit_seconds: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// General Question Interface
interface GeneralQuestion {
  id: number;
  question_text: string;
  question_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Question {
    id: number;
    question_text: string;
    question_image?: string | null;
    question_image_url?: string | null;
    option_a: string;
    option_a_image?: string | null;
    option_a_image_url?: string | null;
    option_b: string;
    option_b_image?: string | null;
    option_b_image_url?: string | null;
    option_c: string;
    option_c_image?: string | null;
    option_c_image_url?: string | null;
    option_d: string;
    option_d_image?: string | null;
    option_d_image_url?: string | null;
    correct_option: string;
    is_active: boolean;
    created_at: string;
}

const AdminQuestionsView = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<'mcq' | 'video' | 'general'>('mcq');
  
  // MCQ Questions state
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [questionToDelete, setQuestionToDelete] = useState<Question | null>(null);
  
  // Video Questions state
  const [videoQuestions, setVideoQuestions] = useState<VideoQuestion[]>([]);
  const [videoSearchTerm, setVideoSearchTerm] = useState<string>('');
  const [showVideoAddModal, setShowVideoAddModal] = useState<boolean>(false);
  const [editingVideoQuestion, setEditingVideoQuestion] = useState<VideoQuestion | null>(null);
  const [videoSubmitting, setVideoSubmitting] = useState<boolean>(false);
  const [showVideoDeleteModal, setShowVideoDeleteModal] = useState<boolean>(false);
  const [videoQuestionToDelete, setVideoQuestionToDelete] = useState<VideoQuestion | null>(null);
  const [videoFormData, setVideoFormData] = useState({
    question_text: '',
    question_order: 1,
    time_limit_seconds: 120,
    is_active: true
  });

  // General Questions state
  const [generalQuestions, setGeneralQuestions] = useState<GeneralQuestion[]>([]);
  const [generalSearchTerm, setGeneralSearchTerm] = useState<string>('');
  const [showGeneralAddModal, setShowGeneralAddModal] = useState<boolean>(false);
  const [editingGeneralQuestion, setEditingGeneralQuestion] = useState<GeneralQuestion | null>(null);
  const [generalSubmitting, setGeneralSubmitting] = useState<boolean>(false);
  const [showGeneralDeleteModal, setShowGeneralDeleteModal] = useState<boolean>(false);
  const [generalQuestionToDelete, setGeneralQuestionToDelete] = useState<GeneralQuestion | null>(null);
  const [generalFormData, setGeneralFormData] = useState({
    question_text: '',
    question_order: 1,
    is_active: true
  });
  
  const [formData, setFormData] = useState({
    question_text: '',
    question_image: null as File | null,
    option_a: '',
    option_a_image: null as File | null,
    option_b: '',
    option_b_image: null as File | null,
    option_c: '',
    option_c_image: null as File | null,
    option_d: '',
    option_d_image: null as File | null,
    correct_option: '',
    is_active: true
  });
  
  const [imagePreviews, setImagePreviews] = useState({
    question_image: null as string | null,
    option_a_image: null as string | null,
    option_b_image: null as string | null,
    option_c_image: null as string | null,
    option_d_image: null as string | null,
  });
  
  useEffect(() => {
    getQuestionsList();
    getVideoQuestionsList();
    getGeneralQuestionsList();
  }, []);

  const getQuestionsList = async () => {
    setIsLoading(true);
    try {
      const response: AxiosResponse<Question[]> = await fetchAllQuestions();
      console.log('Questions : ', response.data);
      setQuestions(response.data);
      setIsLoading(false);
      setError('');
    } catch (error: any) {
      console.log(error);
      setError(error.message || 'Failed to load questions');
      toast.error("Failed to load questions.");
      setIsLoading(false);
    }
  };

  const getVideoQuestionsList = async () => {
    try {
      const response: AxiosResponse<VideoQuestion[]> = await fetchVideoQuestions();
      console.log('Video Questions : ', response.data);
      setVideoQuestions(response.data);
    } catch (error: any) {
      console.log(error);
      toast.error("Failed to load video questions.");
    }
  };

  const getGeneralQuestionsList = async () => {
    try {
      const response: AxiosResponse<GeneralQuestion[]> = await fetchGeneralQuestions();
      console.log('General Questions : ', response.data);
      setGeneralQuestions(response.data);
    } catch (error: any) {
      console.log(error);
      toast.error("Failed to load general questions.");
    }
  };

  const filteredQuestions = questions.filter(q => 
    q.question_text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeCount = questions.filter(q => q.is_active).length;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.question_text.trim()) {
      toast.error('Question text is required');
      return false;
    }
    if (!formData.option_a.trim() || !formData.option_b.trim() || 
        !formData.option_c.trim() || !formData.option_d.trim()) {
      toast.error('All options are required');
      return false;
    }
    if (!formData.correct_option) {
      toast.error('Please select the correct answer option');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
  
    setSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append('question_text', formData.question_text);
      submitData.append('option_a', formData.option_a);
      submitData.append('option_b', formData.option_b);
      submitData.append('option_c', formData.option_c);
      submitData.append('option_d', formData.option_d);
      submitData.append('correct_option', formData.correct_option);
      submitData.append('is_active', String(formData.is_active));
      
      // Add all images if present
      if (formData.question_image) {
        submitData.append('question_image', formData.question_image);
      }
      if (formData.option_a_image) {
        submitData.append('option_a_image', formData.option_a_image);
      }
      if (formData.option_b_image) {
        submitData.append('option_b_image', formData.option_b_image);
      }
      if (formData.option_c_image) {
        submitData.append('option_c_image', formData.option_c_image);
      }
      if (formData.option_d_image) {
        submitData.append('option_d_image', formData.option_d_image);
      }
  
      if (editingQuestion) {
        const response: AxiosResponse<Question> = await updateQuestion(editingQuestion.id, submitData);
        toast.success("Question updated successfully.");
        setQuestions(prev => 
          prev.map(q => q.id === editingQuestion.id ? response.data : q)
        );
      } else {
        const response: AxiosResponse<Question> = await createQuestion(submitData);
        toast.success("Question created successfully.");
        setQuestions(prev => [response.data, ...prev]);
      }
      
      handleCloseModal();
    } catch (error: any) {
      console.log(error);
      toast.error(error.message || "Failed to save question. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleImageChange = (fieldName: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      
      setFormData(prev => ({ ...prev, [fieldName]: file }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => ({
          ...prev,
          [fieldName]: reader.result as string
        }));
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleRemoveImage = (fieldName: string) => {
    setFormData(prev => ({ ...prev, [fieldName]: null }));
    setImagePreviews(prev => ({ ...prev, [fieldName]: null }));
  };

const handleEdit = (question: Question) => {
  setEditingQuestion(question);
  setFormData({
    question_text: question.question_text,
    question_image: null,
    option_a: question.option_a,
    option_a_image: null,
    option_b: question.option_b,
    option_b_image: null,
    option_c: question.option_c,
    option_c_image: null,
    option_d: question.option_d,
    option_d_image: null,
    correct_option: question.correct_option,
    is_active: question.is_active
  });
  
  // FIX: Set image previews to existing URLs from the question
  setImagePreviews({
    question_image: question.question_image_url || null,
    option_a_image: question.option_a_image_url || null,
    option_b_image: question.option_b_image_url || null,
    option_c_image: question.option_c_image_url || null,
    option_d_image: question.option_d_image_url || null,
  });
  
  setShowAddModal(true);
};

  const handleToggleActive = async (question: Question) => {
    try {
      const newStatus = !question.is_active;
      const response: AxiosResponse<Question> = await toggleQuestionStatus(question.id, newStatus);
      toast.success("Status updated");
      console.log('Question status updated:', response.data);
      setQuestions(prev => 
        prev.map(q => q.id === question.id 
          ? { ...q, is_active: newStatus }
          : q
        )
      );
      setError('');
    } catch (error: any) {
      console.log(error);
      toast.error(error.message || "Failed to update status");
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingQuestion(null);
    setFormData({
      question_text: '',
      question_image: null,
      option_a: '',
      option_a_image: null,
      option_b: '',
      option_b_image: null,
      option_c: '',
      option_c_image: null,
      option_d: '',
      option_d_image: null,
      correct_option: '',
      is_active: true
    });
    
    // FIX 2: Clear all image previews when closing modal
    setImagePreviews({
      question_image: null,
      option_a_image: null,
      option_b_image: null,
      option_c_image: null,
      option_d_image: null,
    });
  };

  // Video Question Handlers
  const filteredVideoQuestions = videoQuestions
    .filter(q => q.question_text.toLowerCase().includes(videoSearchTerm.toLowerCase()))
    .sort((a, b) => a.question_order - b.question_order);

  const videoActiveCount = videoQuestions.filter(q => q.is_active).length;

  const handleVideoInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setVideoFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              (name === 'question_order' || name === 'time_limit_seconds') ? parseInt(value) || 0 : value
    }));
  };

  const validateVideoForm = (): boolean => {
    if (!videoFormData.question_text.trim()) {
      toast.error('Question text is required');
      return false;
    }
    if (videoFormData.question_order < 1 || videoFormData.question_order > 10) {
      toast.error('Question order must be between 1 and 10');
      return false;
    }
    if (videoFormData.time_limit_seconds < 30 || videoFormData.time_limit_seconds > 600) {
      toast.error('Time limit must be between 30 and 600 seconds');
      return false;
    }
    return true;
  };

  const handleVideoSubmit = async () => {
    if (!validateVideoForm()) return;

    setVideoSubmitting(true);
    try {
      if (editingVideoQuestion) {
        const response: AxiosResponse<VideoQuestion> = await updateVideoQuestion(editingVideoQuestion.id, videoFormData);
        toast.success("Video question updated successfully.");
        // Update state with response data
        setVideoQuestions(prev => 
          prev.map(q => q.id === editingVideoQuestion.id ? response.data : q)
        );
      } else {
        const response: AxiosResponse<VideoQuestion> = await createVideoQuestion(videoFormData);
        toast.success("Video question created successfully.");
        // Add new question to the list - refetch to ensure proper sorting and complete data
        await getVideoQuestionsList();
      }
      
      handleCloseVideoModal();
    } catch (error: any) {
      console.log(error);
      toast.error(error.message || "Failed to save video question. Please try again.");
    } finally {
      setVideoSubmitting(false);
    }
  };

  const handleEditVideoQuestion = (question: VideoQuestion) => {
    setEditingVideoQuestion(question);
    setVideoFormData({
      question_text: question.question_text,
      question_order: question.question_order,
      time_limit_seconds: question.time_limit_seconds,
      is_active: question.is_active
    });
    setShowVideoAddModal(true);
  };

  const handleToggleVideoActive = async (question: VideoQuestion) => {
    try {
      const newStatus = !question.is_active;
      await updateVideoQuestion(question.id, { is_active: newStatus });
      toast.success("Status updated");
      // Refetch to ensure UI is in sync with backend
      await getVideoQuestionsList();
    } catch (error: any) {
      console.log(error);
      toast.error(error.message || "Failed to update status");
    }
  };

  const handleCloseVideoModal = () => {
    setShowVideoAddModal(false);
    setEditingVideoQuestion(null);
    setVideoFormData({
      question_text: '',
      question_order: 1,
      time_limit_seconds: 120,
      is_active: true
    });
  };

  // General Question Handlers
  const filteredGeneralQuestions = generalQuestions
    .filter(q => q.question_text.toLowerCase().includes(generalSearchTerm.toLowerCase()))
    .sort((a, b) => a.question_order - b.question_order);

  const generalActiveCount = generalQuestions.filter(q => q.is_active).length;

  const handleGeneralInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setGeneralFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              name === 'question_order' ? parseInt(value) || 0 : value
    }));
  };

  const validateGeneralForm = (): boolean => {
    if (!generalFormData.question_text.trim()) {
      toast.error('Question text is required');
      return false;
    }
    if (generalFormData.question_order < 1) {
      toast.error('Question order must be at least 1');
      return false;
    }
    return true;
  };

  const handleGeneralSubmit = async () => {
    if (!validateGeneralForm()) return;

    setGeneralSubmitting(true);
    try {
      if (editingGeneralQuestion) {
        const response: AxiosResponse<GeneralQuestion> = await updateGeneralQuestion(editingGeneralQuestion.id, generalFormData);
        toast.success("General question updated successfully.");
        setGeneralQuestions(prev => 
          prev.map(q => q.id === editingGeneralQuestion.id ? response.data : q)
        );
      } else {
        const response: AxiosResponse<GeneralQuestion> = await createGeneralQuestion(generalFormData);
        toast.success("General question created successfully.");
        await getGeneralQuestionsList();
      }
      
      handleCloseGeneralModal();
    } catch (error: any) {
      console.log(error);
      toast.error(error.message || "Failed to save general question. Please try again.");
    } finally {
      setGeneralSubmitting(false);
    }
  };

  const handleEditGeneralQuestion = (question: GeneralQuestion) => {
    setEditingGeneralQuestion(question);
    setGeneralFormData({
      question_text: question.question_text,
      question_order: question.question_order,
      is_active: question.is_active
    });
    setShowGeneralAddModal(true);
  };

  const handleToggleGeneralActive = async (question: GeneralQuestion) => {
    try {
      const newStatus = !question.is_active;
      await updateGeneralQuestion(question.id, { is_active: newStatus });
      toast.success("Status updated");
      await getGeneralQuestionsList();
    } catch (error: any) {
      console.log(error);
      toast.error(error.message || "Failed to update status");
    }
  };

  const handleCloseGeneralModal = () => {
    setShowGeneralAddModal(false);
    setEditingGeneralQuestion(null);
    setGeneralFormData({
      question_text: '',
      question_order: 1,
      is_active: true
    });
  };

  const handleDeleteGeneralQuestion = async () => {
    if (generalQuestionToDelete) {
      try {
        await deleteGeneralQuestion(generalQuestionToDelete.id);
        toast.success("General question deleted");
        await getGeneralQuestionsList();
      } catch {
        toast.error("Failed to delete general question");
      } finally {
        setShowGeneralDeleteModal(false);
        setGeneralQuestionToDelete(null);
      }
    }
  };

  const handleDeleteVideoQuestion = async () => {
    if (videoQuestionToDelete) {
      try {
        await deleteVideoQuestion(videoQuestionToDelete.id);
        toast.success("Video question deleted");
        // Refetch to ensure UI is in sync with backend
        await getVideoQuestionsList();
      } catch {
        toast.error("Failed to delete video question");
      } finally {
        setShowVideoDeleteModal(false);
        setVideoQuestionToDelete(null);
      }
    }
  };

  if (isLoading) {
    return <Backdrop />;
  }

  if (error) {
    return <ErrorBox errorMessage={error} />;
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header */}
            <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Question Bank</h1>
            <p className="text-gray-600">Manage your test questions pool</p>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-6">
              <button
                onClick={() => setActiveTab('mcq')}
                className={`flex items-center px-6 py-3 font-medium text-sm border-b-2 transition-all ${
                  activeTab === 'mcq'
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="w-5 h-5 mr-2" />
                MCQ Questions
                <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                  activeTab === 'mcq' ? 'bg-brand-100 text-brand-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {questions.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('video')}
                className={`flex items-center px-6 py-3 font-medium text-sm border-b-2 transition-all ${
                  activeTab === 'video'
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Video className="w-5 h-5 mr-2" />
                Video Questions
                <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                  activeTab === 'video' ? 'bg-brand-100 text-brand-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {videoQuestions.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('general')}
                className={`flex items-center px-6 py-3 font-medium text-sm border-b-2 transition-all ${
                  activeTab === 'general'
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                General Questions
                <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                  activeTab === 'general' ? 'bg-brand-100 text-brand-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {generalQuestions.length}
                </span>
              </button>
            </div>

            {/* MCQ Questions Tab */}
            {activeTab === 'mcq' && (
              <>
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-100 rounded-lg shadow-sm p-6">
                    <div className="text-sm text-gray-600 mb-1">Total Questions</div>
                    <div className="text-3xl font-bold text-brand-500">{questions.length}</div>
                </div>
                <div className="bg-gray-100 rounded-lg shadow-sm p-6">
                    <div className="text-sm text-gray-600 mb-1">Active Questions</div>
                    <div className="text-3xl font-bold text-green-600">{activeCount}</div>
                </div>
                <div className="bg-gray-100 rounded-lg shadow-sm p-6">
                    <div className="text-sm text-gray-600 mb-1">Inactive Questions</div>
                    <div className="text-3xl font-bold text-red-400">{questions.length - activeCount}</div>
                </div>
                </div>

                {/* Controls */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Search questions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    </div>
                    <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center justify-center px-4 py-2 bg-brand-500 hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-500 text-white font-semibold rounded-lg transition-all"
                    >
                    <Plus className="w-5 h-5 mr-2" />
                    Add Question
                    </button>
                </div>
                </div>

            {/* Questions List */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {filteredQuestions.length === 0 ? (
                <div className="text-center py-12">
                <p className="text-gray-500">
                    {searchTerm ? 'No questions found matching your search.' : 'No questions yet. Add your first question!'}
                </p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Question
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Correct Option
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                        </th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {filteredQuestions.map((question) => (
                        <tr key={question.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-lg">
                            {question.question_text}
                            </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            {question.correct_option ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                                Option {question.correct_option}
                            </span>
                            ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                                Not Set
                            </span>
                            )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <button
                            onClick={() => handleToggleActive(question)}
                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                question.is_active
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                            }`}
                            >
                            {question.is_active ? (
                                <>
                                <Check className="w-4 h-4 mr-1" />
                                Active
                                </>
                            ) : (
                                <>
                                <X className="w-4 h-4 mr-1" />
                                Inactive
                                </>
                            )}
                            </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                            onClick={() => handleEdit(question)}
                            className="text-brand-500 hover:text-brand-600 mr-4"
                            title="Edit"
                            >
                            <Edit2 className="w-5 h-5" />
                            </button>
                            <button
                            onClick={() => {
                            setQuestionToDelete(question);
                            setShowDeleteModal(true);
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                            >
                            <Trash2 className="w-5 h-5" />
                        </button>
                        </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                </div>
            )}
            </div>
            
            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div
                className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50"
                onClick={() => setShowDeleteModal(false)}
                >
                <div
                    className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full relative"
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                    onClick={() => setShowDeleteModal(false)}
                    >
                    <X className="w-5 h-5" />
                    </button>
                    <h2 className="text-lg font-bold mb-4">Confirm Deletion</h2>
                    <p className="text-gray-600 mb-6">
                    Are you sure you want to delete the question: <strong>{questionToDelete?.question_text}</strong>?
                    </p>
                    <div className="flex justify-end gap-4">
                    <button
                        onClick={() => setShowDeleteModal(false)}
                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={async () => {
                        if (questionToDelete) {
                            try {
                            await deleteQuestion(questionToDelete.id);
                            toast.success("Question deleted");
                            setQuestions((prev) => prev.filter((q) => q.id !== questionToDelete.id));
                            } catch {
                            toast.error("Failed to delete question");
                            } finally {
                            setShowDeleteModal(false);
                            setQuestionToDelete(null);
                            }
                        }
                        }}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                    >
                        Delete
                    </button>
                    </div>
                </div>
                </div>
            )}
              </>
            )}

            {/* Video Questions Tab */}
            {activeTab === 'video' && (
              <>
                {/* Video Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-100 rounded-lg shadow-sm p-6">
                    <div className="text-sm text-gray-600 mb-1">Total Video Questions</div>
                    <div className="text-3xl font-bold text-brand-500">{videoQuestions.length}</div>
                  </div>
                  <div className="bg-gray-100 rounded-lg shadow-sm p-6">
                    <div className="text-sm text-gray-600 mb-1">Active Questions</div>
                    <div className="text-3xl font-bold text-green-600">{videoActiveCount}</div>
                  </div>
                  <div className="bg-gray-100 rounded-lg shadow-sm p-6">
                    <div className="text-sm text-gray-600 mb-1">Inactive Questions</div>
                    <div className="text-3xl font-bold text-red-400">{videoQuestions.length - videoActiveCount}</div>
                  </div>
                </div>

                {/* Video Controls */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        placeholder="Search video questions..."
                        value={videoSearchTerm}
                        onChange={(e) => setVideoSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      onClick={() => {
                        // Set default order to next available number
                        const maxOrder = videoQuestions.length > 0 
                          ? Math.max(...videoQuestions.map(q => q.question_order)) 
                          : 0;
                        setVideoFormData(prev => ({
                          ...prev,
                          question_order: maxOrder + 1
                        }));
                        setShowVideoAddModal(true);
                      }}
                      className="flex items-center justify-center px-4 py-2 bg-brand-500 hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-500 text-white font-semibold rounded-lg transition-all"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Add Video Question
                    </button>
                  </div>
                </div>

                {/* Video Questions List */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  {filteredVideoQuestions.length === 0 ? (
                    <div className="text-center py-12">
                      <Video className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">
                        {videoSearchTerm ? 'No video questions found matching your search.' : 'No video questions yet. Add your first video question!'}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Order
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Question
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Time Limit
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredVideoQuestions.map((question) => (
                            <tr key={question.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-100 text-brand-700 font-semibold">
                                  {question.question_order}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-900 max-w-lg">
                                  {question.question_text}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                  <Clock className="w-4 h-4 mr-1" />
                                  {Math.floor(question.time_limit_seconds / 60)}:{(question.time_limit_seconds % 60).toString().padStart(2, '0')}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  onClick={() => handleToggleVideoActive(question)}
                                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                    question.is_active
                                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                  }`}
                                >
                                  {question.is_active ? (
                                    <>
                                      <Check className="w-4 h-4 mr-1" />
                                      Active
                                    </>
                                  ) : (
                                    <>
                                      <X className="w-4 h-4 mr-1" />
                                      Inactive
                                    </>
                                  )}
                                </button>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() => handleEditVideoQuestion(question)}
                                  className="text-brand-500 hover:text-brand-600 mr-4"
                                  title="Edit"
                                >
                                  <Edit2 className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => {
                                    setVideoQuestionToDelete(question);
                                    setShowVideoDeleteModal(true);
                                  }}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete"
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Video Delete Confirmation Modal */}
                {showVideoDeleteModal && (
                  <div
                    className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50"
                    onClick={() => setShowVideoDeleteModal(false)}
                  >
                    <div
                      className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full relative"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                        onClick={() => setShowVideoDeleteModal(false)}
                      >
                        <X className="w-5 h-5" />
                      </button>
                      <h2 className="text-lg font-bold mb-4">Confirm Deletion</h2>
                      <p className="text-gray-600 mb-6">
                        Are you sure you want to delete the video question: <strong>{videoQuestionToDelete?.question_text}</strong>?
                      </p>
                      <div className="flex justify-end gap-4">
                        <button
                          onClick={() => setShowVideoDeleteModal(false)}
                          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDeleteVideoQuestion}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Video Add/Edit Modal */}
                {showVideoAddModal && (
                  <div 
                    className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto"
                    onClick={handleCloseVideoModal}
                  >
                    <div 
                      className="bg-white rounded-lg shadow-xl max-w-xl w-full my-8"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-6 border-b flex items-center justify-between">
                        <div className="flex items-center">
                          <Video className="w-6 h-6 text-brand-500 mr-3" />
                          <h2 className="text-2xl font-bold text-gray-900">
                            {editingVideoQuestion ? 'Edit Video Question' : 'Add Video Question'}
                          </h2>
                        </div>
                        <button
                          onClick={handleCloseVideoModal}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <X className="w-6 h-6" />
                        </button>
                      </div>

                      <div className="p-6 space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Question Text *
                          </label>
                          <textarea
                            name="question_text"
                            value={videoFormData.question_text}
                            onChange={handleVideoInputChange}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="Enter your video question..."
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Question Order *
                            </label>
                            <input
                              type="number"
                              name="question_order"
                              value={videoFormData.question_order}
                              onChange={handleVideoInputChange}
                              min={1}
                              max={10}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                            <p className="mt-1 text-xs text-gray-500">Order in which question appears (1-10)</p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Time Limit (seconds) *
                            </label>
                            <input
                              type="number"
                              name="time_limit_seconds"
                              value={videoFormData.time_limit_seconds}
                              onChange={handleVideoInputChange}
                              min={30}
                              max={600}
                              step={30}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              {Math.floor(videoFormData.time_limit_seconds / 60)} min {videoFormData.time_limit_seconds % 60} sec
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              name="is_active"
                              checked={videoFormData.is_active}
                              onChange={handleVideoInputChange}
                              className="w-5 h-5 text-brand-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm font-medium text-gray-700">
                              Active (Include in video test)
                            </span>
                          </label>
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t">
                          <button
                            onClick={handleCloseVideoModal}
                            disabled={videoSubmitting}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleVideoSubmit}
                            disabled={videoSubmitting}
                            className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-lg transition-all disabled:opacity-50 flex items-center"
                          >
                            {videoSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {editingVideoQuestion ? 'Update Question' : 'Add Question'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

        {/* Add/Edit Modal */}
        {showAddModal && (
            <div 
                className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto"
                onClick={handleCloseModal}
            >
            <div 
                className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-8"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                    {editingQuestion ? 'Edit Question' : 'Add New Question'}
                </h2>
                <button
                    onClick={handleCloseModal}
                    className="text-gray-500 hover:text-gray-700"
                >
                    <X className="w-6 h-6" />
                </button>
                </div>

                <div className="p-6 space-y-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question Text *
                    </label>
                    <textarea
                    name="question_text"
                    value={formData.question_text}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Enter your question..."
                    />
                </div>
                  {/* Image Upload Section */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question Image (Optional)
                    </label>
                    <div className="mt-1 flex items-center gap-4">
                    {imagePreviews.question_image || (editingQuestion?.question_image_url && !formData.question_image) ? (
                        <div className="relative">
                            <img
                                src={imagePreviews.question_image || editingQuestion?.question_image_url || ''}
                                alt="Question preview"
                                className="h-32 w-32 object-cover rounded-lg border-2 border-gray-300"
                            />
                            <button
                                type="button"
                                onClick={() => handleRemoveImage('question_image')}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg">
                            <label className="cursor-pointer flex flex-col items-center">
                                <Plus className="w-8 h-8 text-gray-400" />
                                <span className="text-xs text-gray-500 mt-2">Upload Image</span>
                                <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageChange('question_image', e)}
                                className="hidden"
                                />
                            </label>
                        </div>
                    )}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                    PNG, JPG, GIF up to 5MB
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Option A */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Option A *
                        </label>
                        <input
                            type="text"
                            name="option_a"
                            value={formData.option_a}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent mb-2"
                        />
                        
                        <div className="mt-2">
                            {imagePreviews.option_a_image || (editingQuestion?.option_a_image_url && !formData.option_a_image) ? (
                            <div className="relative inline-block">
                                <img
                                src={imagePreviews.option_a_image || editingQuestion?.option_a_image_url || ''}
                                alt="Option A"
                                className="h-20 w-20 object-cover rounded border-2 border-gray-300"
                                />
                                <button
                                type="button"
                                onClick={() => handleRemoveImage('option_a_image')}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                >
                                <X className="w-3 h-3" />
                                </button>
                            </div>
                            ) : (
                            <label className="cursor-pointer inline-flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm">
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Image
                                <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageChange('option_a_image', e)}
                                className="hidden"
                                />
                            </label>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Option B *
                        </label>
                        <input
                            type="text"
                            name="option_b"
                            value={formData.option_b}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                        <div className="mt-2">
                            {imagePreviews.option_b_image || (editingQuestion?.option_b_image_url && !formData.option_b_image) ? (
                            <div className="relative inline-block">
                                <img
                                src={imagePreviews.option_b_image || editingQuestion?.option_b_image_url || ''}
                                alt="Option B"
                                className="h-20 w-20 object-cover rounded border-2 border-gray-300"
                                />
                                <button
                                type="button"
                                onClick={() => handleRemoveImage('option_b_image')}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                >
                                <X className="w-3 h-3" />
                                </button>
                            </div>
                            ) : (
                            <label className="cursor-pointer inline-flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm">
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Image
                                <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageChange('option_b_image', e)}
                                className="hidden"
                                />
                            </label>
                            )}
                        </div>
                    </div>
                    

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Option C *
                        </label>
                        <input
                            type="text"
                            name="option_c"
                            value={formData.option_c}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                        <div className="mt-2">
                            {imagePreviews.option_c_image || (editingQuestion?.option_c_image_url && !formData.option_c_image) ? (
                            <div className="relative inline-block">
                                <img
                                src={imagePreviews.option_c_image || editingQuestion?.option_c_image_url || ''}
                                alt="Option C"
                                className="h-20 w-20 object-cover rounded border-2 border-gray-300"
                                />
                                <button
                                type="button"
                                onClick={() => handleRemoveImage('option_c_image')}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                >
                                <X className="w-3 h-3" />
                                </button>
                            </div>
                            ) : (
                            <label className="cursor-pointer inline-flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm">
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Image
                                <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageChange('option_c_image', e)}
                                className="hidden"
                                />
                            </label>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Option D *
                        </label>
                        <input
                            type="text"
                            name="option_d"
                            value={formData.option_d}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                        <div className="mt-2">
                            {imagePreviews.option_d_image || (editingQuestion?.option_d_image_url && !formData.option_d_image) ? (
                            <div className="relative inline-block">
                                <img
                                src={imagePreviews.option_d_image || editingQuestion?.option_d_image_url || ''}
                                alt="Option D"
                                className="h-20 w-20 object-cover rounded border-2 border-gray-300"
                                />
                                <button
                                type="button"
                                onClick={() => handleRemoveImage('option_d_image')}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                >
                                <X className="w-3 h-3" />
                                </button>
                            </div>
                            ) : (
                            <label className="cursor-pointer inline-flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm">
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Image
                                <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageChange('option_d_image', e)}
                                className="hidden"
                                />
                            </label>
                            )}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Correct Answer *
                    </label>
                    <select
                        name="correct_option"
                        value={formData.correct_option}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                        <option value="">Please select an option</option>
                        <option value="A">Option A</option>
                        <option value="B">Option B</option>
                        <option value="C">Option C</option>
                        <option value="D">Option D</option>
                    </select>
                    </div>

                    <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                        <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                        className="w-5 h-5 text-brand-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">
                        Active (Include in question pool)
                        </span>
                    </label>
                    </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                    onClick={handleCloseModal}
                    disabled={submitting}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all disabled:opacity-50"
                    >
                    Cancel
                    </button>
                    <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-lg transition-all disabled:opacity-50 flex items-center"
                    >
                    {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {editingQuestion ? 'Update Question' : 'Add Question'}
                    </button>
                </div>
                </div>
            </div>
            </div>
        )}

        {/* General Questions Tab */}
            {activeTab === 'general' && (
              <>
                {/* General Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-100 rounded-lg shadow-sm p-6">
                    <div className="text-sm text-gray-600 mb-1">Total General Questions</div>
                    <div className="text-3xl font-bold text-brand-500">{generalQuestions.length}</div>
                  </div>
                  <div className="bg-gray-100 rounded-lg shadow-sm p-6">
                    <div className="text-sm text-gray-600 mb-1">Active Questions</div>
                    <div className="text-3xl font-bold text-green-600">{generalActiveCount}</div>
                  </div>
                  <div className="bg-gray-100 rounded-lg shadow-sm p-6">
                    <div className="text-sm text-gray-600 mb-1">Inactive Questions</div>
                    <div className="text-3xl font-bold text-red-400">{generalQuestions.length - generalActiveCount}</div>
                  </div>
                </div>

                {/* General Controls */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        placeholder="Search general questions..."
                        value={generalSearchTerm}
                        onChange={(e) => setGeneralSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      onClick={() => {
                        const maxOrder = generalQuestions.length > 0 
                          ? Math.max(...generalQuestions.map(q => q.question_order)) 
                          : 0;
                        setGeneralFormData(prev => ({
                          ...prev,
                          question_order: maxOrder + 1
                        }));
                        setShowGeneralAddModal(true);
                      }}
                      className="flex items-center justify-center px-4 py-2 bg-brand-500 hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-500 text-white font-semibold rounded-lg transition-all"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Add General Question
                    </button>
                  </div>
                </div>

                {/* General Questions List */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  {filteredGeneralQuestions.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">
                        {generalSearchTerm ? 'No general questions found matching your search.' : 'No general questions yet. Add your first general question!'}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Order
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Question
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredGeneralQuestions.map((question) => (
                            <tr key={question.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-100 text-brand-700 font-semibold">
                                  {question.question_order}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-900 max-w-lg">
                                  {question.question_text}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  onClick={() => handleToggleGeneralActive(question)}
                                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                    question.is_active
                                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                  }`}
                                >
                                  {question.is_active ? (
                                    <>
                                      <Check className="w-4 h-4 mr-1" />
                                      Active
                                    </>
                                  ) : (
                                    <>
                                      <X className="w-4 h-4 mr-1" />
                                      Inactive
                                    </>
                                  )}
                                </button>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() => handleEditGeneralQuestion(question)}
                                  className="text-brand-500 hover:text-brand-600 mr-4"
                                  title="Edit"
                                >
                                  <Edit2 className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => {
                                    setGeneralQuestionToDelete(question);
                                    setShowGeneralDeleteModal(true);
                                  }}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete"
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* General Delete Confirmation Modal */}
                {showGeneralDeleteModal && (
                  <div
                    className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50"
                    onClick={() => setShowGeneralDeleteModal(false)}
                  >
                    <div
                      className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full relative"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                        onClick={() => setShowGeneralDeleteModal(false)}
                      >
                        <X className="w-5 h-5" />
                      </button>
                      <h2 className="text-lg font-bold mb-4">Confirm Deletion</h2>
                      <p className="text-gray-600 mb-6">
                        Are you sure you want to delete the general question: <strong>{generalQuestionToDelete?.question_text}</strong>?
                      </p>
                      <div className="flex justify-end gap-4">
                        <button
                          onClick={() => setShowGeneralDeleteModal(false)}
                          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDeleteGeneralQuestion}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* General Add/Edit Modal */}
                {showGeneralAddModal && (
                  <div 
                    className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto"
                    onClick={handleCloseGeneralModal}
                  >
                    <div 
                      className="bg-white rounded-lg shadow-xl max-w-2xl w-full my-8"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-6">
                        <div className="flex justify-between items-center mb-6">
                          <h2 className="text-2xl font-bold text-gray-900">
                            {editingGeneralQuestion ? 'Edit General Question' : 'Add General Question'}
                          </h2>
                          <button
                            onClick={handleCloseGeneralModal}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-6 h-6" />
                          </button>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Question Text *
                            </label>
                            <textarea
                              name="question_text"
                              value={generalFormData.question_text}
                              onChange={handleGeneralInputChange}
                              rows={4}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              placeholder="Enter the question text..."
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Question Order *
                              </label>
                              <input
                                type="number"
                                name="question_order"
                                value={generalFormData.question_order}
                                onChange={handleGeneralInputChange}
                                min="1"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              />
                            </div>

                            <div className="flex items-center">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  name="is_active"
                                  checked={generalFormData.is_active}
                                  onChange={handleGeneralInputChange}
                                  className="w-5 h-5 text-brand-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm font-medium text-gray-700">
                                  Active (Include in questionnaire)
                                </span>
                              </label>
                            </div>
                          </div>

                          <div className="flex justify-end gap-3 pt-4 border-t">
                            <button
                              onClick={handleCloseGeneralModal}
                              disabled={generalSubmitting}
                              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleGeneralSubmit}
                              disabled={generalSubmitting}
                              className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-lg transition-all disabled:opacity-50 flex items-center"
                            >
                              {generalSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                              {editingGeneralQuestion ? 'Update Question' : 'Add Question'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            </div>
        </div>
    </div>
  );
};

export default AdminQuestionsView;